import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './theme/ThemeContext';
import Login from './components/Login';
import MobileLogin from './components/MobileLogin';
import Layout from './components/Layout';
import ResponsiveLayout from './components/ResponsiveLayout';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductMovements from './pages/ProductMovements';
import ProductInventoryClosing from './pages/ProductInventoryClosing';
import Users from './pages/Users';
import Customers from './pages/Customers';
import POS from './pages/POS';
import Transactions from './pages/Transactions';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import { APP_NAME } from './constants/app';
import { apiUtils } from './services/api';
import useResponsive from './hooks/useResponsive';

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('Dashboard');

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    // Update document title based on current page
    const pageTitle = currentPage.charAt(0).toUpperCase() + currentPage.slice(1);
    document.title = user ? `${pageTitle} - ${APP_NAME}` : APP_NAME;
  }, [currentPage, user]);

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentPage('Dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    setCurrentPage('Dashboard');
    // Use centralized logout utility that handles localStorage removal and redirect
    apiUtils.logout();
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const renderPage = () => {
    switch (currentPage.toLowerCase()) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products />;
      case 'product movements':
        return <ProductMovements />;
      case 'inventory closing':
        return <ProductInventoryClosing />;
      case 'users':
        return <Users />;
      case 'customers':
        return <Customers />;
      case 'pos':
        return <POS />;
      case 'transactions':
        return <Transactions />;
      case 'reports':
        return <Reports />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };

  // Component to handle responsive login
  const ResponsiveLoginWrapper = () => {
    return (
      <ThemeProvider userId={null}>
        <ResponsiveLoginComponent onLogin={handleLogin} />
      </ThemeProvider>
    );
  };

  // Component to conditionally render mobile or desktop login
  const ResponsiveLoginComponent = ({ onLogin }) => {
    // We need to use the hook inside a component that's wrapped by ThemeProvider
    const { isMobile } = useResponsive();
    
    return isMobile ? (
      <MobileLogin onLogin={onLogin} />
    ) : (
      <Login onLogin={onLogin} />
    );
  };

  // Component to handle responsive layout
  const ResponsiveLayoutWrapper = () => {
    return (
      <ThemeProvider userId={user?.id || user?.username}>
        <ResponsiveLayoutComponent />
      </ThemeProvider>
    );
  };

  // Component to conditionally render mobile or desktop layout
  const ResponsiveLayoutComponent = () => {
    // We need to use the hook inside a component that's wrapped by ThemeProvider
    const { isMobile } = useResponsive();
    
    const LayoutComponent = isMobile ? ResponsiveLayout : Layout;
    
    return (
      <LayoutComponent 
        user={user} 
        onLogout={handleLogout}
        currentPage={currentPage}
        onNavigate={handleNavigate}
      >
        {renderPage()}
      </LayoutComponent>
    );
  };

  if (!user) {
    return <ResponsiveLoginWrapper />;
  }

  return <ResponsiveLayoutWrapper />;
}

export default App;
